import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const SEEN_KEY = "ih_splash_seen_v1";
export const SPLASH_TOTAL_MS = 1400;

/**
 * Brand splash overlay shown only on the first visit of the session.
 * - Logo reveal (blur + scale-in), thin brass underline draw, micro-tagline
 * - Total: ~1100ms hold + 400ms fade-out
 * - Locks page scroll only while visible
 */
export default function BrandSplash() {
  const [show, setShow] = useState(() => {
    if (typeof window === "undefined") return false;
    return sessionStorage.getItem(SEEN_KEY) !== "1";
  });

  useEffect(() => {
    if (!show) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => {
      sessionStorage.setItem(SEEN_KEY, "1");
      setShow(false);
    }, SPLASH_TOTAL_MS - 400);
    return () => {
      clearTimeout(t);
      document.body.style.overflow = prev;
    };
  }, [show]);

  // Restore overflow when exit animation completes
  const handleExitComplete = () => {
    document.body.style.overflow = "";
  };

  return (
    <AnimatePresence onExitComplete={handleExitComplete}>
      {show && (
        <motion.div
          data-testid="brand-splash"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-0 z-[200] bg-[#08080A] flex items-center justify-center"
          aria-hidden="true"
        >
          {/* grain texture */}
          <div
            className="absolute inset-0 opacity-[0.06] mix-blend-overlay pointer-events-none"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
            }}
          />
          {/* faint warm glow */}
          <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-[600px] w-[600px] rounded-full bg-[#C48F56] blur-[200px] opacity-10 pointer-events-none" />

          <div className="relative flex flex-col items-center gap-7">
            <motion.img
              src="/brand/invest-hospitality-logo.svg"
              alt=""
              initial={{ opacity: 0, scale: 0.94, filter: "blur(10px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
              className="h-14 md:h-16 w-auto"
              draggable={false}
            />

            <motion.span
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 88, opacity: 1 }}
              transition={{
                duration: 0.55,
                delay: 0.45,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="h-px bg-[#C48F56]"
            />

            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.55 }}
              className="font-mono text-[10px] uppercase tracking-[0.45em] text-white/55"
            >
              Milano · dal 2014
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** True when the splash has already been shown in this session. */
export function hasSeenSplash() {
  if (typeof window === "undefined") return true;
  return sessionStorage.getItem(SEEN_KEY) === "1";
}
